export type DateOnlyString = `${number}-${number}-${number}`;

export type DateOnlyInput = Date | DateOnlyString | string;

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

function pad(value: number): string {
  return value.toString().padStart(2, '0');
}

function assertValid(dateString: string): asserts dateString is DateOnlyString {
  if (!DATE_ONLY_PATTERN.test(dateString)) {
    throw new Error(`Invalid date-only string: ${dateString}`);
  }
}

export function formatDateOnly(date: Date): DateOnlyString {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  return `${year}-${pad(month)}-${pad(day)}` as DateOnlyString;
}

export function parseDateOnly(dateString: string | Date): Date {
  // Handle multiple input formats for robustness across environments
  let cleanDateString: string;

  // Handle Date objects that might come from Supabase in some environments
  if (dateString instanceof Date) {
    return new Date(dateString.getFullYear(), dateString.getMonth(), dateString.getDate());
  }

  // At this point, dateString is guaranteed to be a string
  cleanDateString = dateString;

  // If it's a timestamp format, extract just the date part
  if (cleanDateString.includes('T')) {
    cleanDateString = cleanDateString.split('T')[0];
  }

  // Handle ISO date strings that might have timezone info
  if (cleanDateString.includes('+') || cleanDateString.includes('Z')) {
    cleanDateString = cleanDateString.split('T')[0];
  }

  assertValid(cleanDateString);
  const [, year, month, day] = cleanDateString.match(DATE_ONLY_PATTERN)!;

  const result = new Date(Number(year), Number(month) - 1, Number(day));
  if (formatDateOnly(result) !== cleanDateString) {
    throw new Error(`Invalid calendar date: ${cleanDateString}`);
  }

  result.setHours(0, 0, 0, 0);
  return result;
}

/**
 * True for a YYYY-MM-DD string that is also a real calendar date (2026-02-30 is not).
 */
export function isValidDateOnly(value: string): value is DateOnlyString {
  try {
    parseDateOnly(value);
    return DATE_ONLY_PATTERN.test(value);
  } catch {
    return false;
  }
}

/**
 * Normalize arbitrary date input (Date or ISO-ish string) to a date-only string (YYYY-MM-DD).
 * Ensures downstream consumers always work with consistent date-only values.
 */
export function toDateOnlyString(input: DateOnlyInput): DateOnlyString {
  if (input instanceof Date) {
    return formatDateOnly(input);
  }

  return formatDateOnly(parseDateOnly(input));
}

/**
 * Convert arbitrary date input to a Date object anchored at local midnight.
 * This avoids timezone shifts that occur when using the Date constructor directly on ISO strings.
 */
export function toDateOnlyDate(input: DateOnlyInput): Date {
  if (input instanceof Date) {
    return new Date(input.getFullYear(), input.getMonth(), input.getDate());
  }

  return parseDateOnly(input);
}

/**
 * Convert arbitrary date input to a Date object fixed at 12:00 UTC.
 * Useful when consistent UTC handling is required (e.g., formatting with explicit UTC timezone).
 */
export function toDateOnlyUtcDate(input: DateOnlyInput): Date {
  const dateString = toDateOnlyString(input);
  return toMiddayUTC(dateString);
}

/**
 * Helper mirroring the common `date.toISOString().split('T')[0]` pattern without timezone pitfalls.
 */
export function toDateOnlyIsoString(input: DateOnlyInput): DateOnlyString {
  return toDateOnlyString(input);
}

export function addDays(dateString: string | Date, amount: number): DateOnlyString {
  if (!Number.isInteger(amount)) {
    throw new Error('Amount must be an integer');
  }

  const base = parseDateOnly(dateString);
  base.setDate(base.getDate() + amount);

  return formatDateOnly(base);
}

export function getWeekStart(
  dateString: string | Date,
  firstDayOfWeek: number = 1,
): DateOnlyString {
  if (typeof dateString === 'string') {
    assertValid(dateString);
  }
  if (firstDayOfWeek < 0 || firstDayOfWeek > 6) {
    throw new Error('firstDayOfWeek must be between 0 (Sunday) and 6 (Saturday)');
  }

  const date = parseDateOnly(dateString);
  const dayOfWeek = date.getDay();

  const offset = (dayOfWeek - firstDayOfWeek + 7) % 7;
  date.setDate(date.getDate() - offset);

  return formatDateOnly(date);
}

export function getWeekEnd(dateString: string | Date, firstDayOfWeek: number = 1): DateOnlyString {
  const start = getWeekStart(dateString, firstDayOfWeek);
  return addDays(start, 6);
}

export function toMiddayUTC(dateString: string): Date {
  const [, year, month, day] = dateString.match(DATE_ONLY_PATTERN)!;
  return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), 12, 0, 0, 0));
}

const DEFAULT_LOCALE = 'en-US';

const DEFAULT_DISPLAY_FORMAT: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
};

function resolveLocale(locale?: string): string {
  if (locale) {
    return locale;
  }

  if (typeof navigator !== 'undefined' && navigator.language) {
    return navigator.language;
  }

  return DEFAULT_LOCALE;
}

/**
 * Convert a weekday number (0=Sunday, per JS/PostgreSQL convention) to a
 * localized day name using the Intl API.
 */
export function formatWeekday(weekday: number, locale: string): string {
  // Build a known-Sunday date and offset by weekday number
  const sunday = new Date(2026, 0, 4); // 2026-01-04 is a Sunday
  const date = new Date(sunday);
  date.setDate(sunday.getDate() + weekday);
  return new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(date);
}

export function formatDateForDisplay(
  dateString: DateOnlyString,
  locale?: string,
  options?: Intl.DateTimeFormatOptions,
): string {
  assertValid(dateString);

  const displayLocale = resolveLocale(locale);
  const baseOptions = options ? { ...options } : { ...DEFAULT_DISPLAY_FORMAT };
  const formatOptions: Intl.DateTimeFormatOptions = {
    ...baseOptions,
    timeZone: 'UTC',
  };

  return new Intl.DateTimeFormat(displayLocale, formatOptions).format(toMiddayUTC(dateString));
}
